import { computed, makeAutoObservable } from 'mobx'
import { v4 as uuidv4 } from 'uuid'
import { range } from 'ramda'
import EmailAttachmentModel from './Attachment'
import azEmailService from '../services/azureGraphEmailService'
import DOMPurify from 'dompurify'

/**
 * A model representing an Email
 */
class EmailModel {
  /** The id fo the email **/
  public readonly id: string = uuidv4()

  /** the inbox address from which we should be loading the email attachments **/
  public readonly mailboxAddress: string

  public readonly hasAttachments = false

  public readonly contentType: string = 'text/html'
  public readonly rawHTMLBody: string = ''

  /** list of attachments that represents the attached files **/
  public attachments: EmailAttachmentModel[] = []

  /** List of attachments that represent the inline attachments that go into the body of the email, like images **/
  public inlineAttachments: EmailAttachmentModel[] = []

  constructor(data: MicrosoftEmail, mailboxAddress: string) {
    this.mailboxAddress = mailboxAddress

    const { id, hasAttachments, body } = data

    Object.assign(this, { id, hasAttachments })

    if (body?.content) {
      this.rawHTMLBody = data.body.content
    }

    makeAutoObservable(this)
  }

  /**
   * A representation
   */
  @computed
  get contentBody(): string {
    console.log('parsing email body')
    const parser = new DOMParser()
    const el = parser.parseFromString(this.rawHTMLBody, 'text/html').body

    // const el = document.createElement('html')
    // el.innerHTML = this.rawHTMLBody

    const imgEls = el.getElementsByTagName('img')

    range(0, imgEls.length).forEach((i) => {
      const imgEl = imgEls.item(i)!
      const srcContentId = imgEl.src?.replace('cid:', '')

      const attachmentObj = this.getInlineAttachmentByContentId(srcContentId)

      if (attachmentObj && attachmentObj.binaryContent) {
        // const imgBase64 = `data:${attachmentObj.contentType};base64,${attachmentObj.binaryContent}`
        imgEl.src = URL.createObjectURL(new Blob([attachmentObj.binaryContent], { type: 'text/html' }))
      }
    })

    return DOMPurify.sanitize(el.innerHTML, { USE_PROFILES: { html: true } })
  }

  /** Loads all the attachments (attached filed + inline) **/
  public async loadAllAttachments() {
    const attachments = await azEmailService.listEmailAttachments(this.mailboxAddress, this.id)

    const models = attachments
      .filter((a) => !a.isInline)
      .map((a) => {
        const m = new EmailAttachmentModel(this.mailboxAddress, a)
        m.parent = this
        return m
      })

    this.attachments = models.filter((a) => !a.isInline)
    this.inlineAttachments = models.filter((a) => a.isInline)
  }

  public async lazyLoadInlineAttachments() {
    const ids = this.inlineAttachments.map((a) => a.id)

    if (ids.length < 1) {
      console.debug('no inline attachments to load')
      return
    }

    const rsp = await azEmailService.listEmailBodyAttachments({
      mailboxEmailAddress: this.mailboxAddress,
      emailId: this.id,
      attachmentIds: ids
    })

    for (const attIdx in rsp) {
      const att = rsp[attIdx]
      this.inlineAttachments[attIdx].binaryContent = att.buffer
    }
  }

  private getInlineAttachmentByContentId(contentId: string): EmailAttachmentModel | null {
    for (const att of this.inlineAttachments) {
      if (att.contentId === contentId) {
        return att
      }
    }

    return null
  }
}

export default EmailModel
